/* The console's motion: the DESKTOP'S motion, ported, not re-imagined.
 *
 * 2026-09-23, operator: "overdrive the console the same way, also ensure
 * that the desktop and console are aligned with surgical precision". The
 * desktop's Overview became a living instrument on 2026-09-06, and every
 * one of its rules is restated here so the two applications move as one
 * product:
 *
 *   - A figure that changes ROLLS through real intermediate values to the
 *     new reading (apps/desktop/src/components/Tween.tsx): exponential
 *     ease-out over --dur-slow, integers stay integers on the way, and it
 *     always lands on the real value. It never rolls on first paint -- the
 *     desktop's Tween starts AT its value -- because a number counting up
 *     from zero on load is a number the page invented for a second.
 *   - An arc SWEEPS to its fraction (the desktop's score ring): from empty
 *     the first time, from the previous reading after that, over
 *     --dur-slow x 3 on --ease-expo.
 *   - A chart line DRAWS ITSELF IN once, left to right, the hours arriving
 *     in the order they happened (the desktop's hero sparklines).
 *   - A table's rows LAND a beat apart the first time it fills, in the
 *     order the server sent them.
 *
 * WHY THIS IS KEYED. The desktop's components persist across updates; the
 * console's modules REBUILD a card on every poll (live.js, fifteen seconds)
 * and replace the old nodes. Without a key every poll would replay every
 * entrance -- a dashboard that re-performs itself every fifteen seconds.
 * So each figure, arc, chart and table is remembered by what it IS (its
 * card's heading plus its own label), and only a changed reading moves.
 *
 * Everything reads --dur-slow, which tokens.css zeroes under
 * prefers-reduced-motion -- the desktop's switch, so one OS setting stills
 * both applications. At zero this module does nothing at all.
 */

const FIGURES = ".qf-value, .q-badge-num, .gauge-value, .fig-num, .stat-value, .score-num, .tile-value";
const ARCS = ".gauge-fill";
const TABLE_BODIES = ".card table tbody";

function durSlow() {
  const raw = getComputedStyle(document.documentElement).getPropertyValue("--dur-slow").trim();
  const n = parseFloat(raw);
  if (!Number.isFinite(n)) return 320;
  return raw.endsWith("ms") ? n : n * 1000;
}

function easeExpoOut(p) {
  return p === 1 ? 1 : 1 - Math.pow(2, -10 * p);
}

/** What an element IS, stable across rebuilds: its card's heading and the
 *  nearest label beside it. */
function keyOf(el) {
  const card = el.closest(".card, .q-figures, section, [data-card]");
  const head = card?.querySelector("h2, h3")?.textContent?.trim() ?? "";
  let label = "";
  const parent = el.parentElement;
  if (parent) {
    for (const sib of parent.children) {
      if (sib !== el && /(^|-)label\b/.test(sib.className)) {
        label = sib.textContent.trim();
        break;
      }
    }
    if (!label) label = parent.getAttribute("aria-label") ?? "";
  }
  if (!label && el.classList.contains("gauge-fill")) {
    label = el.closest(".gauge")?.getAttribute("aria-label")?.split(":")[0] ?? "";
  }
  return `${location.pathname}|${head}|${label}|${el.className}`;
}

// ---------------------------------------------------------------- figures

const lastFigure = new Map();
/// Text nodes this module is writing, so its own frames are not mistaken
/// for a new reading by the observer below.
const rolling = new WeakSet();

/** The figure's leading number, if it IS a number (not "—", not "3/900"). */
function leadingNumber(el) {
  const t = el.firstChild;
  if (!t || t.nodeType !== Node.TEXT_NODE) return null;
  const m = /^(-?\d+(?:\.(\d+))?)$/.exec(t.textContent.trim());
  if (!m) return null;
  return { node: t, value: Number(m[1]), decimals: m[2] ? m[2].length : 0 };
}

function rollFigure(el) {
  const now = leadingNumber(el);
  if (!now) return;
  const key = keyOf(el);
  const before = lastFigure.get(key);
  lastFigure.set(key, now.value);
  if (before === undefined || before === now.value) return;
  const dur = durSlow();
  if (dur <= 0) return;
  const t0 = performance.now();
  rolling.add(now.node);
  // Show the PREVIOUS reading until the first frame: a rebuilt element
  // arrives carrying the new value, and without this it can paint once at
  // 40 before rolling up from 12 (seen driving it).
  now.node.textContent = before.toFixed(now.decimals);
  const step = (t) => {
    if (!now.node.isConnected) return rolling.delete(now.node);
    // Clamped below too: a frame's timestamp can precede t0, and the
    // ease turns negative progress into a negative figure (-19 on the way
    // from 12 to 40, found by driving this). Tween.tsx carries the same fix.
    const p = Math.max(0, Math.min(1, (t - t0) / dur));
    const v = before + (now.value - before) * easeExpoOut(p);
    now.node.textContent = p === 1 ? now.value.toFixed(now.decimals) : v.toFixed(now.decimals);
    if (p < 1) requestAnimationFrame(step);
    else rolling.delete(now.node);
  };
  requestAnimationFrame(step);
}

// ------------------------------------------------------------------- arcs

const lastArc = new Map();

function sweepArc(path) {
  const target = parseFloat(path.getAttribute("stroke-dashoffset"));
  const full = parseFloat(String(path.getAttribute("stroke-dasharray")).split(/[\s,]+/)[0]);
  if (!Number.isFinite(target) || !Number.isFinite(full)) return;
  const key = keyOf(path);
  const from = lastArc.has(key) ? lastArc.get(key) : full;
  lastArc.set(key, target);
  if (from === target) return;
  const dur = durSlow() * 3;
  if (dur <= 0) return;
  path.animate(
    [{ strokeDashoffset: String(from) }, { strokeDashoffset: String(target) }],
    { duration: dur, easing: "cubic-bezier(0.16, 1, 0.3, 1)" },
  );
}

// ----------------------------------------------------------------- charts

const drawnCharts = new Set();

function drawChart(svg) {
  const key = keyOf(svg);
  if (drawnCharts.has(key)) return;
  drawnCharts.add(key);
  const dur = durSlow() * 3;
  if (dur <= 0) return;
  for (const line of svg.querySelectorAll("path[fill='none'], polyline")) {
    line.setAttribute("pathLength", "1");
    line.style.strokeDasharray = "1";
    line.animate([{ strokeDashoffset: "1" }, { strokeDashoffset: "0" }], {
      duration: dur,
      easing: "cubic-bezier(0.16, 1, 0.3, 1)",
      fill: "backwards",
    });
  }
}

// ----------------------------------------------------------------- tables

const filledTables = new Set();

function landRows(tbody) {
  const key = keyOf(tbody);
  if (filledTables.has(key)) return;
  if (tbody.rows.length === 0) return;
  filledTables.add(key);
  const dur = durSlow();
  if (dur <= 0) return;
  [...tbody.rows].slice(0, 24).forEach((row, i) => {
    row.animate(
      [
        { opacity: 0, transform: "translateY(6px)" },
        { opacity: 1, transform: "none" },
      ],
      { duration: dur * 1.5, delay: i * 35, easing: "cubic-bezier(0.16, 1, 0.3, 1)", fill: "backwards" },
    );
  });
}

// -------------------------------------------------------------- the watch

function visit(root) {
  if (!(root instanceof Element)) return;
  const pick = (sel, fn) => {
    if (root.matches(sel)) fn(root);
    root.querySelectorAll(sel).forEach(fn);
  };
  pick(FIGURES, rollFigure);
  pick(ARCS, sweepArc);
  pick(TABLE_BODIES, landRows);
  pick(".chart svg", drawChart);
}

function start() {
  visit(document.body);
  new MutationObserver((records) => {
    for (const r of records) {
      if (r.type === "childList") r.addedNodes.forEach(visit);
      // A figure whose TEXT was replaced in place rather than rebuilt.
      else if (r.type === "characterData" && !rolling.has(r.target)) {
        const host = r.target.parentElement;
        if (host?.matches(FIGURES)) rollFigure(host);
      }
    }
  }).observe(document.body, { childList: true, subtree: true, characterData: true });
}

// Modules load after hydration (components/PageScript.tsx), so the page may
// already be complete; the desktop-parity rule every console module follows.
if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", start);
else start();
